import { createRoot } from 'react-dom/client';

import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export type SelectPopupOption<T> = { value: T; label: string; hint?: string };

function SelectOptionDialog<T>({
    title,
    placeholder,
    options,
    onResolve,
}: {
    title: string;
    placeholder: string;
    options: SelectPopupOption<T>[];
    onResolve: (value: SelectPopupOption<T> | undefined) => void;
}) {
    return (
        <Dialog open onOpenChange={(open) => !open && onResolve(undefined)}>
            <DialogContent showCloseButton={false} className="p-0 gap-0 overflow-hidden">
                <DialogHeader className="px-4 pt-4 pb-2">
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <Command>
                    <CommandInput placeholder={placeholder} autoFocus />
                    <CommandList className="max-h-[320px]">
                        <CommandEmpty>Aucun résultat.</CommandEmpty>
                        {options.map((option, index) => (
                            // cmdk identifies and filters items by `value`: keep the label in it (searchable) but
                            // suffix the index so two options sharing a label (e.g. homonymous deliverers) stay distinct.
                            <CommandItem
                                key={`${option.label}#${index}`}
                                value={`${option.label}#${index}`}
                                onSelect={() => onResolve(option)}
                                className="cursor-pointer"
                            >
                                <span className="truncate">{option.label}</span>
                                {option.hint ? (
                                    <span className="ml-auto text-xs text-muted-foreground">{option.hint}</span>
                                ) : null}
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </DialogContent>
        </Dialog>
    );
}

// Imperative searchable picker, same pattern as confirmPopup. Resolves with the chosen option,
// or undefined when dismissed. `value` is caller-defined (it may be null, e.g. "reset to default").
export const selectOptionPopup = <T,>({
    title,
    placeholder = 'Rechercher…',
    options,
}: {
    title: string;
    placeholder?: string;
    options: SelectPopupOption<T>[];
}): Promise<SelectPopupOption<T> | undefined> => {
    return new Promise<SelectPopupOption<T> | undefined>((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);

        function handleResolve(value: SelectPopupOption<T> | undefined) {
            root.unmount();
            container.remove();
            resolve(value);
        }

        root.render(
            <SelectOptionDialog title={title} placeholder={placeholder} options={options} onResolve={handleResolve} />,
        );
    });
};
